import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, makeStyles } from '@material-ui/core';
import colors from '../../../config/colors';
import ImageIcon from '../../shared/Icon/ImageIcon.component';
import { i18nService } from '../../../services/i18n.service';

/** how long the copy button stays on the 'copied' checkmark */
const COPIED_FEEDBACK_MS = 1500;

const useStyles = makeStyles({
	text: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	tooltip: {
		backgroundColor: colors.white,
		color: colors.clockRecordBlue,
		fontFamily: 'RubikRegular',
		fontSize: 14,
		fontWeight: 'normal',
		lineHeight: '1.4em',
		padding: '10px 16px',
		borderRadius: 8,
		boxShadow: '0 2px 12px 0 rgba(40, 44, 111, 0.16)',
		textAlign: 'right',
		//maxWidth: 300,
		wordWrap: 'break-word',
	},
	arrow: {
		color: colors.white,
	},
	content: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'center',
		gap: 8,
	},
	/** selectable so the value can also be picked up by hand, not only by the button */
	value: {
		userSelect: 'text',
	},
	copyButton: {
		background: 'none',
		border: 'none',
		padding: 0,
		margin: 0,
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
});

type EllipsisCellProps = {
	value: any;
};

/**
 * Cell for columns that have no fixed width - the text is ellipsized to the
 * column, and the full value shows in a tooltip, but only when it is actually
 * cut off. The tooltip carries a copy button, since a cut off value is exactly
 * the one that cannot be read off the screen.
 */
const EllipsisCell = ({ value }: EllipsisCellProps) => {
	const classes = useStyles();
	const textRef = useRef<HTMLDivElement>(null);
	const resetTimer = useRef<any>();
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);

	// the tooltip is only opened when the text is actually cut off (elipsized)
	const onOpen = useCallback(() => {
		const el = textRef.current;
		if (el && el.scrollWidth > el.clientWidth) {
			setOpen(true);
		}
	}, []);

	const onClose = useCallback(() => setOpen(false), []);

	// blank cells are safe
	const text = value == null ? '' : String(value);

	const onCopy = useCallback(() => {
		// optional chaining short circuits the whole chain, so a browser
		// without the clipboard api (http origins) just does nothing
		navigator.clipboard
			?.writeText(text)
			.then(() => {
				setCopied(true);
				clearTimeout(resetTimer.current);
				resetTimer.current = setTimeout(
					() => setCopied(false),
					COPIED_FEEDBACK_MS,
				);
			})
			.catch(() => {});
	}, [text]);

	// rows are recycled while paging, so a pending reset has to go with them
	useEffect(() => () => clearTimeout(resetTimer.current), []);

	const copyLabel = i18nService.translate('general.copy');

	return (
		<Tooltip
			arrow
			interactive
			//leaveDelay={200}
			title={
				text ? (
					<div className={classes.content}>
						<button
							type='button'
							className={classes.copyButton}
							onClick={onCopy}
							aria-label={copyLabel}
							title={copyLabel}
						>
							<ImageIcon
								name={copied ? 'checkedMark' : 'clipboardText'}
								width={18}
								height={18}
							/>
						</button>
						<span className={classes.value}>{text}</span>
					</div>
				) : (
					''
				)
			}
			open={open}
			onOpen={onOpen}
			onClose={onClose}
			placement='top'
			classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
		>
			<div ref={textRef} className={classes.text}>
				{text}
			</div>
		</Tooltip>
	);
};

export default EllipsisCell;
